<div class="duna-cprng-orgsync-container">
  <mat-card class="header-card">
    <mat-card-header>
      <mat-card-title><h1>Keycloak org sync</h1></mat-card-title>
      <mat-card-subtitle>
        Cron syncer én forfalden org per minut (forfalden efter 15 min; kø giver typisk ~20 min mellem success). SLA: grøn =
        OK inden for 20 min siden sidste success.
      </mat-card-subtitle>
    </mat-card-header>
    <mat-card-actions align="end">
      <button mat-stroked-button (click)="loadOverview()" [disabled]="loading">
        Opdater
      </button>
      <button
        mat-stroked-button
        color="warn"
        (click)="resetStuckRunning()"
        [disabled]="resettingRunning || loading"
      >
        {{ resettingRunning ? 'Nulstiller…' : 'Nulstil hængende RUNNING' }}
      </button>
      <button
        mat-raised-button
        color="primary"
        (click)="triggerSyncAll()"
        [disabled]="triggeringAll || loading"
      >
        {{ triggeringAll ? 'Sætter i kø…' : 'Sync alle (kæde)' }}
      </button>
    </mat-card-actions>
  </mat-card>

  @if (errorMessage) {
    <mat-card class="message-card message-card--error">
      <mat-card-content>
        <p>{{ errorMessage }}</p>
      </mat-card-content>
    </mat-card>
  }

  @if (successMessage) {
    <mat-card class="message-card message-card--success">
      <mat-card-content>
        <p>{{ successMessage }}</p>
      </mat-card-content>
    </mat-card>
  }

  <mat-card class="table-card">
    <mat-card-content>
      <table mat-table [dataSource]="tableDataSource" class="sync-table">
        <ng-container matColumnDef="org_name">
          <th mat-header-cell *matHeaderCellDef>Organisation</th>
          <td mat-cell *matCellDef="let row">
            <div class="cell-stack">
              <strong>{{ row.org_name ?? row.realm ?? '–' }}</strong>
              <span class="muted">
                #{{ row.organizations_id }}
                @if (row.realm && row.realm !== row.org_name) {
                  · realm {{ row.realm }}
                }
                · {{ row.identity_status }}
              </span>
            </div>
          </td>
        </ng-container>

        <ng-container matColumnDef="last_run_status">
          <th mat-header-cell *matHeaderCellDef>Status</th>
          <td mat-cell *matCellDef="let row">
            <span class="status-chip" [ngClass]="statusClassForRow(row)">
              {{ displayStatus(row) }}
            </span>
            <div class="muted sla-line">{{ slaLabel(row) }}</div>
            @if (row.last_run_trigger_source) {
              <span class="muted source-tag">{{ row.last_run_trigger_source }}</span>
            }
          </td>
        </ng-container>

        <ng-container matColumnDef="progress">
          <th mat-header-cell *matHeaderCellDef>Fremdrift</th>
          <td mat-cell *matCellDef="let row" class="progress-cell">
            <div class="progress-label">
              <span class="phase-code">{{ row.last_run_phase ?? '–' }}</span>
              <span>{{ phaseLabel(row.last_run_phase) }}</span>
            </div>
            <mat-progress-bar
              mode="determinate"
              [value]="progressPercent(row)"
              [class.progress-running]="row.last_run_status === 'running'"
            ></mat-progress-bar>
            <span class="muted percent">{{ progressPercent(row) }}%</span>
            <div class="progress-detail">{{ progressDetail(row) }}</div>
          </td>
        </ng-container>

        <ng-container matColumnDef="errors">
          <th mat-header-cell *matHeaderCellDef>Fejl / bemærkninger</th>
          <td mat-cell *matCellDef="let row" class="errors-cell">
            @if ((row.last_run_users_errors ?? 0) > 0) {
              <button
                mat-stroked-button
                color="warn"
                class="user-errors-btn"
                (click)="openUserErrors(row)"
                [disabled]="!row.last_run_id"
              >
                Brugerfejl ({{ row.last_run_users_errors }})
              </button>
            }
            @if (row.error_summary) {
              <pre class="error-summary" [matTooltip]="row.error_summary">{{ row.error_summary }}</pre>
            } @else if ((row.last_run_users_errors ?? 0) === 0) {
              <span class="muted">–</span>
            }
          </td>
        </ng-container>

        <ng-container matColumnDef="metrics">
          <th mat-header-cell *matHeaderCellDef>Tal</th>
          <td mat-cell *matCellDef="let row">
            @if (row.last_run_id) {
              <div class="metrics-grid">
                <span>OK <strong>{{ row.last_run_users_success ?? 0 }}</strong></span>
                <span>Fejl <strong>{{ row.last_run_users_errors ?? 0 }}</strong></span>
                <span>KC <strong>{{ row.last_run_keycloak_users_count ?? '–' }}</strong></span>
                <span>Efter cleanup <strong>{{ row.last_run_users_after_cleanup ?? '–' }}</strong></span>
                @if (row.last_run_last_user_index != null) {
                  <span>Bruger # <strong>{{ row.last_run_last_user_index }}</strong></span>
                }
              </div>
            } @else {
              –
            }
          </td>
        </ng-container>

        <ng-container matColumnDef="timing">
          <th mat-header-cell *matHeaderCellDef>Tid</th>
          <td mat-cell *matCellDef="let row">
            <div class="cell-stack">
              @if (row.last_run_started_at) {
                <span>Seneste: {{ row.last_run_started_at | date : 'dd/MM/yyyy HH:mm:ss' }}</span>
              }
              @if (row.last_success_at) {
                <span class="muted">OK: {{ row.last_success_at | date : 'dd/MM/yyyy HH:mm:ss' }}</span>
              }
              <span class="muted">{{ elapsedForRow(row) }}</span>
              @if (row.sync_timing?.lines?.length) {
                <span class="kc-timing" [matTooltip]="row.sync_timing!.summary">
                  @for (line of row.sync_timing!.lines; track line) {
                    <span class="kc-timing-line">{{ line }}</span>
                  }
                </span>
              }
            </div>
          </td>
        </ng-container>

        <ng-container matColumnDef="actions">
          <th mat-header-cell *matHeaderCellDef></th>
          <td mat-cell *matCellDef="let row">
            <button
              mat-stroked-button
              color="warn"
              (click)="openUserErrors(row)"
              [disabled]="!row.last_run_id || (row.last_run_users_errors ?? 0) === 0"
              matTooltip="Vis hvilke brugere fejlede og hvorfor"
            >
              <mat-icon>error_outline</mat-icon>
              Fejl
            </button>
            <button
              mat-stroked-button
              (click)="openLog(row)"
              [disabled]="!row.last_run_id"
              matTooltip="Vis struktureret log (faser/trin)"
            >
              <mat-icon>article</mat-icon>
              Log
            </button>
            <button
              mat-stroked-button
              color="primary"
              (click)="triggerSync(row)"
              [disabled]="triggeringId === row.identity_types_id || loading"
              matTooltip="Kør sync nu for denne org"
            >
              <mat-icon>sync</mat-icon>
              Sync
            </button>
          </td>
        </ng-container>

        <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
        <tr mat-row *matRowDef="let row; columns: displayedColumns" [ngClass]="rowClass(row)"></tr>
        <tr class="mat-row" *matNoDataRow>
          <td class="mat-cell" [attr.colspan]="displayedColumns.length">
            {{ loading ? 'Henter…' : 'Ingen Keycloak identity providers fundet.' }}
          </td>
        </tr>
      </table>
    </mat-card-content>
  </mat-card>
</div>
